import { clsx } from '@alexis/helpers/clsx';
import CheckboxItem from '@pages/FlightSearchResult/components/CheckboxItem';
import { DEFAULT_NUMBER_OF_OPTIONS_SHOWED } from '@pages/FlightSearchResult/constants';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import { GenzoTrackApplyFilterActionEventType } from '@pages/FlightSearchResult/types';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { convertFilterStateToDataForTracking } from '@helpers/flight';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type BookingSitesFilterProps = {
  className?: string;
  disableCollapse?: boolean;
  genzoTrackApplyFilterActionEvent: GenzoTrackApplyFilterActionEventType;
  genzoTrackApplyFilterSearchView: () => void;
};

const BookingSitesFilter: React.FC<BookingSitesFilterProps> = ({
  className,
  disableCollapse,
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
}) => {
  const { providerFilterOptions } = useFlightSearchResults();
  const { isPerLegSearch } = useFlightPLS();

  const [isBookingSitesOptionsExpanded, setIsBookingSitesOptionsExpanded] = useState(
    providerFilterOptions.length > DEFAULT_NUMBER_OF_OPTIONS_SHOWED,
  );

  const translations = useSelector(getTranslationsState);

  const { activeLeg, providerCodesFilter, toggleMultiCitySingleFilter } = useFlightSearchFilters();

  const selectedProviderCodesForActiveLeg = useMemo(
    () => providerCodesFilter.find((item) => item.leg === activeLeg)?.values || [],
    [providerCodesFilter, activeLeg],
  );

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  // Removing wego from the fare providers to be displayed under Booking sites filter,
  // The same function can be achieved by using booking options filter
  const fareProvidersToDisplayInBookingSitesFilter = providerFilterOptions.filter(
    (provider) => !provider.name?.toLowerCase().includes('wego'),
  );

  const handleFareProviderClick = (code: string) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      searchParamKey: FlightSearchResultSearchParams.ProviderCodes,
      value: code,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      booking_sites: convertFilterStateToDataForTracking(
        newFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
    });
    genzoTrackApplyFilterSearchView();
  };

  return (
    <div className={clsx(styles.categoryContent, className && styles.inDropdown)}>
      {(disableCollapse || isBookingSitesOptionsExpanded
        ? fareProvidersToDisplayInBookingSitesFilter
        : fareProvidersToDisplayInBookingSitesFilter.slice(0, DEFAULT_NUMBER_OF_OPTIONS_SHOWED)
      ).map((provider) => {
        return (
          <CheckboxItem
            className={className}
            key={provider.code}
            isChecked={selectedProviderCodesForActiveLeg.includes(provider.code)}
            label={provider.name}
            onCheckboxToggle={() => handleFareProviderClick(provider.code)}
          />
        );
      })}

      {!disableCollapse &&
      fareProvidersToDisplayInBookingSitesFilter.length > DEFAULT_NUMBER_OF_OPTIONS_SHOWED ? (
        <div
          className={clsx(styles.showMore, isBookingSitesOptionsExpanded && styles.expanded)}
          onClick={() => setIsBookingSitesOptionsExpanded((prev) => !prev)}
        >
          {isBookingSitesOptionsExpanded ? translations.show_less : translations.show_more}
          <i className={clsx(styles.chevron, styles.bottom)}></i>
        </div>
      ) : null}
    </div>
  );
};

export default BookingSitesFilter;
